import { isEmpty } from 'lodash-es'
import {
  type ComputedRef,
  type Ref,
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onServerPrefetch,
  ref,
} from 'vue'

import { type PageVersionKey, usePostHog } from '@/common/composables/usePostHog'
import { LandingPageContent } from '@/modules/guestUi/interfaces/landingPage'
import { isVideoAssetReady } from '@/modules/residency/utils/media'
import { shareImage, shareImageDimensionsPx } from '@/modules/residency/utils/seo'
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl } from '@/modules/residency/utils/youtube'
import { useSettingStore } from '@/stores/setting'
import { buildMetaDescription } from '@/utils/metaDescription'
import { buildPageTitle } from '@/utils/pageTitle'

export interface UseLandingPageOptions {
  fetchPage: () => Promise<LandingPageContent | null>
  // eslint-disable-next-line no-unused-vars
  buildPagePath: (pageSlug: string) => string
  // eslint-disable-next-line no-unused-vars
  buildJsonLd?: (page: LandingPageContent) => string | null
  // eslint-disable-next-line no-unused-vars
  buildCanonicalPath?: (page: LandingPageContent) => string
  pageVersion: { key: PageVersionKey; version: string }
}

export function useLandingPage({
  fetchPage,
  buildPagePath,
  buildJsonLd,
  buildCanonicalPath = page => buildPagePath(page.slug),
  pageVersion,
}: UseLandingPageOptions): {
  page: Ref<LandingPageContent | null>
  isNotFound: Ref<boolean>
  shareUrl: ComputedRef<string>
} {
  const settingStore = useSettingStore()
  const page = ref<LandingPageContent | null>(null)
  const isNotFound = ref<boolean>(false)
  let isUnmounted = false

  async function fetchPageOrNull(): Promise<LandingPageContent | null> {
    try {
      const fetched = await fetchPage()
      return isEmpty(fetched) ? null : fetched
    } catch (e) {
      return null
    }
  }

  function publishHead(found: LandingPageContent): void {
    settingStore.ssrTitle = buildPageTitle([found.name], settingStore, { locality: true })
    settingStore.ssrCanonicalPath = found.slug ? buildCanonicalPath(found) : null
    settingStore.ssrDescription = buildMetaDescription(
      found.description || found.show?.description || '',
    )
    const videoUrl = found.video_url || ''
    const hostedVideo = isVideoAssetReady(found.video) ? found.video : null
    settingStore.ssrImage =
      shareImage(found.show) || hostedVideo?.poster || getYouTubeThumbnailUrl(videoUrl) || ''
    settingStore.ssrImageDimensionsPx = shareImageDimensionsPx(found.show)
    // og:video only plays an mp4 or an embeddable page, so the hosted HLS manifest never belongs
    // here; hosted-video pages share on the poster in og:image instead.
    settingStore.ssrVideoUrl = videoUrl ? getYouTubeEmbedUrl(videoUrl) : null
    settingStore.ssrJsonLd = buildJsonLd?.(found) ?? null
  }

  // Inert while SSR builds a store per request, but load() also runs client-side, and a half-reset
  // branch is one shared store away from serving another page's description.
  function clearHead(): void {
    settingStore.ssrTitle = settingStore.venue_name
    settingStore.ssrCanonicalPath = null
    settingStore.ssrDescription = null
    settingStore.ssrImage = null
    settingStore.ssrImageDimensionsPx = null
    settingStore.ssrVideoUrl = null
    settingStore.ssrJsonLd = null
  }

  function titleBrowserTab(): void {
    if (settingStore.ssrTitle) document.title = settingStore.ssrTitle
  }

  function tagSessionWithPageVersion(): void {
    usePostHog().registerPageVersionForSession(pageVersion.key, pageVersion.version)
  }

  async function load(): Promise<void> {
    // ssr-render.js turns this flag into an HTTP 404, so it can't be left wherever the previous
    // load put it.
    settingStore.notFound = false
    try {
      page.value = await fetchPageOrNull()
      isNotFound.value = !page.value
      if (page.value) {
        publishHead(page.value)
      } else {
        clearHead()
        settingStore.notFound = true
      }
      if (typeof document !== 'undefined' && !isUnmounted) {
        titleBrowserTab()
        if (page.value) tagSessionWithPageVersion()
      }
    } finally {
      settingStore.ssrPrefetchDone = true
    }
  }

  onServerPrefetch(load)
  onBeforeMount(load)
  onBeforeUnmount(() => (isUnmounted = true))

  const shareUrl = computed<string>(() =>
    page.value ? `https://${settingStore.siteDomain}${buildPagePath(page.value.slug)}` : '',
  )

  return { page, isNotFound, shareUrl }
}
